import { isRemakeOrderItem } from "src/pages/orders/utils/orderItemUtils";

export const REMAKE_TYPES = [
  "Bad Graphic",
  "Damaged",
  "Wrong Item",
  "Missing Item",
];

export const VIP_TABLE_HEAD = [
  { id: "name", label: "VIP Name", alignRight: false },
  { id: "approvedOrders", label: "Approved Orders", alignRight: false },
  { id: "remakeOrders", label: "Remake Orders", alignRight: false },
  ...REMAKE_TYPES.map((type) => ({
    id: type,
    label: type,
    alignRight: false,
  })),
  { id: "action", label: "", alignRight: true },
];

export const createEmptyRemakeCounts = () =>
  Object.fromEntries(REMAKE_TYPES.map((type) => [type, 0]));

export const getTrackingUrl = (trackingNo) => {
  if (!trackingNo) return null;

  if (trackingNo.startsWith("1Z")) {
    return `https://www.ups.com/track?track=yes&trackNums=${trackingNo}&loc=en_US&requester=ST/trackdetails`;
  }

  const digitsOnly = trackingNo.replace(/\D/g, "");
  if (digitsOnly.length === 12) {
    return `https://www.fedex.com/fedextrack/?trknbr=${trackingNo}&trkqual=12029~397781621763~FDEG`;
  }

  return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNo}`;
};

export const formatOrderDate = (dateString) => {
  if (!dateString) return "-";

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return dateString;

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getOrderRemakeFeedbacks = (order) => {
  const feedbacks = [];

  (order?.order_details || []).forEach((item) => {
    const feedback = item?.remake_feedback?.trim();
    if (feedback) feedbacks.push(feedback);
  });

  return feedbacks;
};

export const isRemakeOrder = (order) => {
  if (
    order?.is_remake === 1 ||
    order?.is_remake === "1" ||
    order?.is_remake === true
  ) {
    return true;
  }

  return (order?.order_details || []).some(
    (item) => isRemakeOrderItem(item) || item?.remake_feedback?.trim(),
  );
};

export const getRemakeTypeCountsForOrder = (order) => {
  const counts = createEmptyRemakeCounts();
  const feedbacks = getOrderRemakeFeedbacks(order);

  feedbacks.forEach((feedback) => {
    if (Object.prototype.hasOwnProperty.call(counts, feedback)) {
      counts[feedback] += 1;
    }
  });

  return counts;
};

export const resolveVipUsername = (vip) => {
  if (!vip || typeof vip !== "object") return "";

  const explicit = [vip.username, vip.vip_username, vip.user_name].find(
    (value) => typeof value === "string" && value.trim(),
  );
  if (explicit) return explicit.trim();

  const email = vip.vip_email || vip.email || "";
  if (typeof email === "string" && email.trim()) {
    return email.trim();
  }

  return "";
};

const trimVipField = (value) =>
  typeof value === "string" ? value.trim() : value ?? "";

const loginIdentityPayload = (value) => {
  const trimmed = trimVipField(value);
  if (!trimmed) return {};
  if (trimmed.includes("@")) return { email: trimmed };
  return { username: trimmed };
};

export const buildVipAccountPayload = ({
  id,
  name,
  username,
  pin,
} = {}) => {
  const trimmedName = trimVipField(name);
  const trimmedUsername = trimVipField(username);
  const hasPin = typeof pin === "string" ? pin.length > 0 : Boolean(pin);
  const isUpdate = id != null && id !== "";

  if (!isUpdate) {
    const payload = {
      name: trimmedName,
      username: trimmedUsername,
    };

    if (hasPin) payload.pin = pin;

    return payload;
  }

  const numericId = Number(id);
  const payload = {
    id:
      Number.isFinite(numericId) && String(numericId) === String(id).trim()
        ? numericId
        : id,
    name: trimmedName,
    ...loginIdentityPayload(trimmedUsername),
  };

  if (hasPin) payload.pin = pin;

  return payload;
};

const mapVipStatsRecord = (vip) => {
  const remakeCounts = createEmptyRemakeCounts();
  const feedbackGroupwise =
    vip?.feedback_groupwise || vip?.feedbacks || {};

  Object.entries(feedbackGroupwise).forEach(([feedback, count]) => {
    if (Object.prototype.hasOwnProperty.call(remakeCounts, feedback)) {
      remakeCounts[feedback] = Number(count) || 0;
    }
  });

  const remakeOrdersFromFeedback = Object.values(remakeCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  return {
    id: vip?.vip_id,
    name: vip?.vip_name || `VIP #${vip?.vip_id}`,
    username: resolveVipUsername(vip),
    email: vip?.vip_email || vip?.email || "",
    remakeOrders:
      Number(vip?.total_remake_orders) ||
      Number(vip?.remake_orders) ||
      remakeOrdersFromFeedback,
    approvedOrders:
      Number(vip?.total_approved_orders) ||
      Number(vip?.approved_orders) ||
      0,
    remakeCounts,
    orders: [],
  };
};

const isVipSummaryRecord = (row) =>
  row &&
  typeof row === "object" &&
  row.vip_id != null &&
  (row.feedback_groupwise != null ||
    row.feedbacks != null ||
    row.total_approved_orders != null ||
    row.total_remake_orders != null ||
    row.vip_name != null);

export const parseVipStatsResponse = (response) => {
  const data = response?.data?.data ?? response?.data;

  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    isVipSummaryRecord(data)
  ) {
    return {
      totalOrders: 0,
      orders: [],
      vipSummaries: [mapVipStatsRecord(data)],
    };
  }

  if (Array.isArray(data) && data.length > 0 && isVipSummaryRecord(data[0])) {
    return {
      totalOrders: 0,
      orders: [],
      vipSummaries: data.map(mapVipStatsRecord),
    };
  }

  if (data?.orders && Array.isArray(data.orders)) {
    return {
      totalOrders: data.total_orders ?? data.orders.length,
      orders: data.orders,
      vipSummaries: null,
    };
  }

  if (Array.isArray(data)) {
    return {
      totalOrders: data.length,
      orders: data,
      vipSummaries: null,
    };
  }

  return { totalOrders: 0, orders: [], vipSummaries: null };
};

export const parseVipDetailResponse = (response, fallbackVip) => {
  const parsed = parseVipStatsResponse(response);

  if (parsed.vipSummaries?.length) {
    const match =
      parsed.vipSummaries.find(
        (item) => String(item.id) === String(fallbackVip?.id),
      ) || parsed.vipSummaries[0];

    return {
      ...fallbackVip,
      ...match,
      username: match.username || fallbackVip?.username || "",
    };
  }

  return {
    ...fallbackVip,
    remakeCounts: createEmptyRemakeCounts(),
    approvedOrders: fallbackVip?.approvedOrders ?? 0,
    remakeOrders: 0,
  };
};

export const formatVipDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const buildVipSummaryRows = (vipsAccounts = [], orders = []) => {
  const summaries = new Map();

  vipsAccounts.forEach((vip) => {
    summaries.set(String(vip.id), {
      id: vip.id,
      name: vip.name || `VIP #${vip.id}`,
      username: resolveVipUsername(vip),
      email: vip.email || "",
      remakeOrders: 0,
      approvedOrders: 0,
      remakeCounts: createEmptyRemakeCounts(),
      orders: [],
    });
  });

  orders.forEach((order) => {
    const vipId = order?.vips_approved_by;
    if (vipId == null || vipId === "") return;

    const key = String(vipId);
    if (!summaries.has(key)) {
      summaries.set(key, {
        id: vipId,
        name: `VIP #${vipId}`,
        username: "",
        email: "",
        remakeOrders: 0,
        approvedOrders: 0,
        remakeCounts: createEmptyRemakeCounts(),
        orders: [],
      });
    }

    const summary = summaries.get(key);
    summary.orders.push(order);

    const isApproved =
      order?.vips_approved === 1 ||
      order?.vips_approved === "1" ||
      order?.vips_approved === true;

    if (isApproved) {
      summary.approvedOrders += 1;
    }

    if (isRemakeOrder(order)) {
      summary.remakeOrders += 1;
    }

    const typeCounts = getRemakeTypeCountsForOrder(order);
    REMAKE_TYPES.forEach((type) => {
      summary.remakeCounts[type] += typeCounts[type];
    });
  });

  return Array.from(summaries.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

export const filterVipSummariesByName = (summaries, searchValue) => {
  const query = searchValue?.trim().toLowerCase();
  if (!query) return summaries;

  return summaries.filter(
    (vip) =>
      vip.name?.toLowerCase().includes(query) ||
      vip.username?.toLowerCase().includes(query) ||
      vip.email?.toLowerCase().includes(query),
  );
};

function descendingComparator(a, b, orderBy) {
  const aValue =
    orderBy === "name"
      ? a.name
      : orderBy === "remakeOrders"
        ? a.remakeOrders
        : orderBy === "approvedOrders"
          ? a.approvedOrders
          : a.remakeCounts?.[orderBy] ?? 0;
  const bValue =
    orderBy === "name"
      ? b.name
      : orderBy === "remakeOrders"
        ? b.remakeOrders
        : orderBy === "approvedOrders"
          ? b.approvedOrders
          : b.remakeCounts?.[orderBy] ?? 0;

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

export function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function applySortFilter(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  return stabilizedThis?.map((el) => el[0]);
}

export const filterVipSummariesByRemakeFeedback = (summaries, remakeFeedback) => {
  if (!remakeFeedback) return summaries;

  return summaries.filter(
    (vip) => Number(vip?.remakeCounts?.[remakeFeedback] || 0) > 0,
  );
};
